import * as DateFns from 'date-fns'
import * as DateFnsTz from 'date-fns-tz'

export default class Carbon {

  private date = '';
  private timezone = '';

  get currentDate() {
    return this.date
  }

  get currentTimezone() {
    return this.timezone
  }

  public constructor(date: string, tz: string) {
    this.date = date
    this.timezone = tz
  }

  public static now(tz: string = 'UTC') {
    /**
     * getTimezoneOffset取得的是以分為單位，UTC為基準點，從系統時區校正回基準點所需加減的分鐘數
     */
    let systemTzOffset = -((new Date()).getTimezoneOffset()) // mins
    systemTzOffset = systemTzOffset * 60 * 1000 // millisecs

    const tzOffset = DateFnsTz.getTimezoneOffset(tz)

    const offsetDiff = tzOffset - systemTzOffset

    const converted = offsetDiff >= 0
      ? DateFns.addMilliseconds(Date.now(), Math.abs(offsetDiff))
      : DateFns.subMilliseconds(Date.now(), Math.abs(offsetDiff))

    const now = DateFns.format(converted, 'yyyy-MM-dd HH:mm:ss')

    return new this(now, tz)
  }

  public static parse(date: string | number | Date, tz: string = 'UTC') {
    let parsedDate: string

    if (typeof date === 'string') {
      parsedDate = DateFns.format(new Date(date), 'yyyy-MM-dd HH:mm:ss')
    } else {
      // @ts-ignore
      parsedDate = DateFns.format(date, 'yyyy-MM-dd HH:mm:ss')
    }
    return new this(parsedDate, tz)
  }

  public static today(tz: string = 'UTC') {
    /**
     * getTimezoneOffset取得的是以分為單位，UTC為基準點，從系統時區校正回基準點所需加減的分鐘數
     */
    let systemTzOffset = -((new Date()).getTimezoneOffset()) // mins
    systemTzOffset = systemTzOffset * 60 * 1000 // millisecs

    const tzOffset = DateFnsTz.getTimezoneOffset(tz)

    const offsetDiff = tzOffset - systemTzOffset

    const startOfToday = DateFns.startOfToday()

    const converted = offsetDiff >= 0
      ? DateFns.addMilliseconds(startOfToday, Math.abs(offsetDiff))
      : DateFns.subMilliseconds(startOfToday, Math.abs(offsetDiff))

    let parsedDate = DateFns.format(converted, 'yyyy-MM-dd')
    parsedDate += ' 00:00:00'

    return new this(parsedDate, tz)
  }

  public static yesterday(tz: string = 'UTC') {
    /**
     * getTimezoneOffset取得的是以分為單位，UTC為基準點，從系統時區校正回基準點所需加減的分鐘數
     */
    let systemTzOffset = -((new Date()).getTimezoneOffset()) // mins
    systemTzOffset = systemTzOffset * 60 * 1000 // millisecs

    const tzOffset = DateFnsTz.getTimezoneOffset(tz)

    const offsetDiff = tzOffset - systemTzOffset

    const startOfYesterday = DateFns.subDays(new Date(Date.now()), 1)

    const converted = offsetDiff >= 0
      ? DateFns.addMilliseconds(startOfYesterday, Math.abs(offsetDiff))
      : DateFns.subMilliseconds(startOfYesterday, Math.abs(offsetDiff))

    let parsedDate = DateFns.format(converted, 'yyyy-MM-dd')
    parsedDate += ' 00:00:00'

    return new this(parsedDate, tz)
  }

  public static tomorrow(tz: string = 'UTC') {
    /**
     * getTimezoneOffset取得的是以分為單位，UTC為基準點，從系統時區校正回基準點所需加減的分鐘數
     */
    let systemTzOffset = -((new Date()).getTimezoneOffset()) // mins
    systemTzOffset = systemTzOffset * 60 * 1000 // millisecs

    const tzOffset = DateFnsTz.getTimezoneOffset(tz)

    const offsetDiff = tzOffset - systemTzOffset
    const startOfTomorrow = DateFns.addDays(new Date(Date.now()), 1)

    const converted = offsetDiff >= 0
      ? DateFns.addMilliseconds(startOfTomorrow, Math.abs(offsetDiff))
      : DateFns.subMilliseconds(startOfTomorrow, Math.abs(offsetDiff))

    let parsedDate = DateFns.format(converted, 'yyyy-MM-dd')
    parsedDate += ' 00:00:00'

    return new this(parsedDate, tz)
  }

  public tz(tz: string = 'UTC') {
    if (tz === this.currentTimezone) {
      return this
    }

    const currentOffset = DateFnsTz.getTimezoneOffset(this.timezone)
    const tzOffset = DateFnsTz.getTimezoneOffset(tz)
    this.timezone = tz

    const offsetDiff = tzOffset - currentOffset

    const converted = offsetDiff > 0
      ? DateFns.addMilliseconds(new Date(this.date), Math.abs(offsetDiff))
      : DateFns.subMilliseconds(new Date(this.date), Math.abs(offsetDiff))

    this.date = DateFns.format(converted, 'yyyy-MM-dd HH:mm:ss')

    return this
  }

  public startOfDay() {
    const startOfCurrentDate = DateFns.startOfDay(new Date(this.date))
    this.date = DateFns.format(startOfCurrentDate, 'yyyy-MM-dd HH:mm:ss')

    return this
  }

  public endOfDay() {
    const endOfCurrentDate = DateFns.endOfDay(new Date(this.date))
    this.date = DateFns.format(endOfCurrentDate, 'yyyy-MM-dd HH:mm:ss')

    return this
  }

  public addHours(hours: number) {
    const daysAddedDate = DateFns.addHours(new Date(this.date), hours)
    this.date = DateFns.format(daysAddedDate, 'yyyy-MM-dd HH:mm:ss')

    return this
  }

  public addDay() {
    const dayAddedDate = DateFns.addDays(new Date(this.date), 1)
    this.date = DateFns.format(dayAddedDate, 'yyyy-MM-dd HH:mm:ss')

    return this
  }

  public addDays(days: number) {
    const daysAddedDate = DateFns.addDays(new Date(this.date), days)
    this.date = DateFns.format(daysAddedDate, 'yyyy-MM-dd HH:mm:ss')

    return this
  }

  public subDay() {
    const daySubedDate = DateFns.subDays(new Date(this.date), 1)
    this.date = DateFns.format(daySubedDate, 'yyyy-MM-dd HH:mm:ss')

    return this
  }

  public subDays(days: number) {
    const daysSubedDate = DateFns.subDays(new Date(this.date), days)
    this.date = DateFns.format(daysSubedDate, 'yyyy-MM-dd HH:mm:ss')

    return this
  }

  public addMonth() {
    const dayAddedMonth = DateFns.addMonths(new Date(this.date), 1)
    this.date = DateFns.format(dayAddedMonth, 'yyyy-MM-dd HH:mm:ss')

    return this
  }

  public addMonths(months: number) {
    const dayAddedMonth = DateFns.addMonths(new Date(this.date), months)
    this.date = DateFns.format(dayAddedMonth, 'yyyy-MM-dd HH:mm:ss')

    return this
  }

  public subMonth() {
    const daySubedMonth = DateFns.subMonths(new Date(this.date), 1)
    this.date = DateFns.format(daySubedMonth, 'yyyy-MM-dd HH:mm:ss')

    return this
  }

  public subMonths(months: number) {
    const daySubedMonth = DateFns.subMonths(new Date(this.date), months)
    this.date = DateFns.format(daySubedMonth, 'yyyy-MM-dd HH:mm:ss')

    return this
  }

  public addYear() {
    const dayAddedYear = DateFns.addYears(new Date(this.date), 1)
    this.date = DateFns.format(dayAddedYear, 'yyyy-MM-dd HH:mm:ss')

    return this
  }

  public addYears(years: number) {
    const dayAddedYear = DateFns.addYears(new Date(this.date), years)
    this.date = DateFns.format(dayAddedYear, 'yyyy-MM-dd HH:mm:ss')

    return this
  }

  public subYear() {
    const daySubedYear = DateFns.subYears(new Date(this.date), 1)
    this.date = DateFns.format(daySubedYear, 'yyyy-MM-dd HH:mm:ss')

    return this
  }

  public subYears(years: number) {
    const daySubedYear = DateFns.subYears(new Date(this.date), years)
    this.date = DateFns.format(daySubedYear, 'yyyy-MM-dd HH:mm:ss')

    return this
  }

  public equalTo(date: Carbon) {
    const currentDate = new Date(this.date)
    const dateToCompare = new Date(date.currentDate)
    return DateFns.isEqual(currentDate, dateToCompare)
  }

  public notEqualTo(date: Carbon) {
    const currentDate = new Date(this.date)
    const dateToCompare = new Date(date.currentDate)
    return !DateFns.isEqual(currentDate, dateToCompare)
  }

  public greaterThan(date: Carbon) {
    const currentDate = new Date(this.date)
    const dateToCompare = new Date(date.currentDate)
    return DateFns.isAfter(currentDate, dateToCompare)
  }

  public greaterThanOrEqualTo(date: Carbon) {
    const currentDate = new Date(this.date)
    const dateToCompare = new Date(date.currentDate)
    return DateFns.isAfter(currentDate, dateToCompare) || DateFns.isEqual(currentDate, dateToCompare)
  }

  public lessThan(date: Carbon) {
    const currentDate = new Date(this.date)
    const dateToCompare = new Date(date.currentDate)
    return DateFns.isBefore(currentDate, dateToCompare)
  }

  public lessThanOrEqualTo(date: Carbon) {
    const currentDate = new Date(this.date)
    const dateToCompare = new Date(date.currentDate)
    return DateFns.isBefore(currentDate, dateToCompare) || DateFns.isEqual(currentDate, dateToCompare)
  }

  public toDateString() {
    return DateFns.format(new Date(this.date), 'yyyy-MM-dd')
  }

  public toDateTimeString() {
    return DateFns.format(new Date(this.date), 'yyyy-MM-dd HH:mm:ss')
  }

  public toTimeString() {
    return DateFns.format(new Date(this.date), 'T')
  }

  public toIso8601String() {
    return DateFns.format(new Date(this.date), "yyyy-MM-dd'T'HH:mm:ssxxx")
  }

}
